/* eslint-disable consistent-return */
import { TABLE_KEYS } from '@/shared/components/Table/models/Filter.model'
import Table from '@/shared/components/Table/Table'
import Tile from '@/shared/components/Tile/Tile'
import Grid from '@config/Grid/Grid'
import { useTranslation } from 'react-i18next'
import TableCardModal from '@/shared/components/Modal/TableCardModal/TableCardModal'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { rgbaStringToHsva } from '@uiw/color-convert'
import { getColumnsConfig } from '@/features/table-cards/TablesColumns.model'
import styles from '@/features/table-cards/TableCardsManagement.module.scss'
import { useTableCardsQuery } from '@/features/table-cards/hooks/useTableCardsQuery'
import { usePlannerTables } from '@/features/table-cards/hooks/usePlannerTables'
import { useFloorPlanStore } from '@/features/floor-plan'
import { primaryColors } from '@/assets/styles/helpers/colors.styles'
import {
  qrCodeCardsPostPayloadModel,
  qrCodeCardsPutPayloadModel
} from '@/features/table-cards/api/TableCardsManagementAPI.model'
import TableCardsList from '@/features/table-cards/components/TableCardsList/TableCardsList'
import TablesModal from '@/shared/components/Modal/TablesModal/TablesModal'
import { useTableCardStore } from '@/features/table-cards/store/useTableCardStore'
import CardsPrintLayout, {
  ExtendedQrCodeCardModel
} from '@/features/table-cards/components/CardsPrintLayout/CardsPrintLayout'
import useMobileAppManagementQuery from '@/features/mobile-app/hooks/useMobileAppManagementQuery'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import PrintGridModal from '@/shared/components/Modal/PrintGridModal/PrintGridModal'

function TableCardsManagement() {
  const { t } = useTranslation()
  const columnsConfig = useMemo(() => getColumnsConfig(t), [])
  const columnsModel = useMemo(() => ['id', 'number', 'room'], [])
  const [isTableCardModalOpen, setIsTableCardModalOpen] = useState(false)
  const [isTablesModalOpen, setIsTablesModalOpen] = useState(false)
  const [isPrintGridModalOpen, setIsPrintGridModalOpen] = useState(false)

  const emptyFilterModel = useMemo(() => ({ items: [] }), [])

  const resetIsDirty = useFormDirtyStore(s => s.reset)
  useEffect(() => resetIsDirty(), [])

  const openModal = () => {
    setIsTableCardModalOpen(true)
  }

  const { tableCardsList } = useTableCardsQuery()
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])
  const [noEdit, setNoEdit] = useState(false)

  const [selectedTableCards, setSelectedTableCards] = useState<string[]>([])

  const [selectedCards, setSelectedCards] = useState<ExtendedQrCodeCardModel[]>(
    []
  )

  const printRef = useRef<HTMLDivElement | null>(null)

  const [isEditing, setIsEditing] = useState(false)
  const { postTableCard, putTableCard, isTablesListLoading } =
    useTableCardsQuery()

  const {
    setId,
    setTableNumber,
    setTableId,
    setTitle,
    setDescription,
    setShapeValue,
    setBackgroundColor,
    setContentColor,
    setContrastColor,
    setWithNfc,
    setLanguageCode,
    setAlignment,
    setBackgroundImage,
    setWidthValue,
    setHeightValue,
    setDiameterValue,
    setTableIds
  } = useTableCardStore()

  const previewFunction = useCallback(
    (qrCodeCardId: string, tableNumber: number, tableId?: string) => {
      const qrCard = tableCardsList?.find(card => card.id === qrCodeCardId)
      if (qrCard) {
        const {
          id,
          title,
          description,
          shape,
          alignment = 'left',
          withNfc,
          languageCode,
          contentColor,
          contrastColor,
          backgroundColor,
          backgroundImage,
          width,
          height,
          diameter,
          tableIds
        } = qrCard

        setId(id)
        setTableNumber(tableNumber)
        setTableId(tableId)
        setTitle(title)
        setDescription(description)
        setBackgroundColor(rgbaStringToHsva(backgroundColor))
        setContentColor(rgbaStringToHsva(contentColor))
        setContrastColor(rgbaStringToHsva(contrastColor))
        setWithNfc(!!withNfc)
        setLanguageCode(languageCode)
        setAlignment(alignment)
        setBackgroundImage(
          backgroundImage
            ? `${backgroundImage.url}?t=${backgroundImage.version}&alt=media`
            : null
        )
        setWidthValue(width || 0)
        setHeightValue(height || 0)
        setDiameterValue(diameter || 0)
        setShapeValue(shape)
        setAlignment(alignment)
        setTableIds(tableIds)
        setIsTableCardModalOpen(true)

        useTableCardStore.getState().setBaseline({
          id,
          tableNumber,
          tableId,
          shapeValue: shape,
          backgroundColor: rgbaStringToHsva(backgroundColor),
          contentColor: rgbaStringToHsva(contentColor),
          contrastColor: rgbaStringToHsva(contrastColor),
          withNfc: !!withNfc,
          languageCode,
          alignment,
          backgroundImage: backgroundImage
            ? `${backgroundImage.url}?t=${backgroundImage.version}&alt=media`
            : null,
          widthValue: width || 0,
          heightValue: height || 0,
          diameterValue: diameter || 0,
          title,
          description,
          tableIds
        })
      }
    },
    [
      setAlignment,
      setBackgroundColor,
      setContentColor,
      setContrastColor,
      setDiameterValue,
      setId,
      setTableId,
      setTableNumber,
      setLanguageCode,
      setShapeValue,
      setWithNfc,
      setBackgroundImage,
      setWidthValue,
      setHeightValue,
      setTableIds,
      setTitle,
      setDescription,
      tableCardsList
    ]
  )

  const plannerTables = usePlannerTables()
  const deleteFloorTable = useFloorPlanStore(s => s.deleteTable)

  const mappedTablesList = useMemo(
    () =>
      plannerTables.map(table => {
        // No matching backend table -> no QR code to print yet.
        if (!table.hasQr) {
          return {
            number: table.number,
            id: table.id,
            room: table.room,
            tableCard: [
              {
                id: table.id,
                label: 'No QR',
                icon: (
                  <i
                    className="las la-exclamation-circle icon-medium"
                    style={{ color: primaryColors.orange }}
                  />
                ),
                color: 'secondary'
              }
            ]
          }
        }

        return {
          number: table.number,
          id: table.id,
          room: table.room,
          tableCard: table.qrCodeCardId
            ? [
                {
                  id: table.id,
                  label: tableCardsList?.find(
                    card => card.id === table.qrCodeCardId
                  )?.title,
                  previewFunction: () => {
                    setNoEdit(true)
                    previewFunction(
                      table.qrCodeCardId as string,
                      table.number,
                      table.tableId
                    )
                  }
                }
              ]
            : [
                {
                  id: table.id,
                  label: 'No Card',
                  icon: (
                    <i
                      className="las la-exclamation-circle icon-medium"
                      style={{ color: primaryColors.orange }}
                    />
                  ),
                  color: 'secondary'
                }
              ]
        }
      }),
    [plannerTables, tableCardsList, previewFunction]
  )

  const submitFunction = useCallback(
    (payload: qrCodeCardsPostPayloadModel | qrCodeCardsPutPayloadModel) => {
      if (isEditing) {
        if ('id' in payload) {
          putTableCard(payload)
          resetIsDirty()
          setIsTableCardModalOpen(false)
        }
      } else {
        postTableCard(payload)
        resetIsDirty()
        setIsTableCardModalOpen(false)
      }
    },
    [isEditing, postTableCard, putTableCard, resetIsDirty]
  )

  // Tables are owned by the planner now, so deleting a row removes the planner
  // table (the matching backend QR table, if any, is managed separately).
  const handleOnDeleteItems = (ids: string[]) => {
    ids.forEach(id => deleteFloorTable(id))
    setSelectedRowIds([])
  }

  const handleSetSelectedExternalRowIds = useCallback(
    (ids: string[]) => setSelectedRowIds(ids),
    []
  )

  const { logoImage } = useMobileAppManagementQuery()
  const logoImageUrl = `${logoImage.data?.imageUrl}?t=${logoImage.data?.version}`

  const handleTablesModalOnClose = () => {
    setIsTablesModalOpen(false)
    useTableCardStore.getState().resetToInitialState()
  }

  return (
    <>
      <Grid>
        <div
          id="tour-tables-list"
          className={`${styles.tableCardsManagementTableContainer} start-2 col-4`}
        >
          <Tile color="black" title={t('TABLE_LIST.TITLE')}>
            <div className={styles.tableCardsManagementTableWrapper}>
              <Table
                initialRows={mappedTablesList || []}
                columns={columnsConfig}
                columnsModel={columnsModel}
                hideColumnFilter
                filtersList={[]}
                hideRowFilter
                tableKey={TABLE_KEYS.DISHES}
                filterModel={emptyFilterModel}
                handleOnAddItem={() => {
                  setIsTablesModalOpen(true)
                }}
                handleOnDeleteItems={handleOnDeleteItems}
                setSelectedExternalRowIds={handleSetSelectedExternalRowIds}
                selectedExternalRowIds={selectedRowIds}
                hideFooter
                disablePagination
                isLoading={isTablesListLoading}
              />
            </div>
          </Tile>
        </div>
        <div
          id="tour-cards-list"
          className={`${styles.tableCardsManagementTableContainer} start-6 col-7`}
        >
          <Tile color="black" title={t('TABLE_CARDS_LIST.TITLE')}>
            <p className={`body-medium ${styles.description}`}>
              {t('TABLE_CARDS_LIST.DESCRIPTION')}
            </p>
            <TableCardsList
              selectedRowIds={selectedRowIds}
              setIsEditing={setIsEditing}
              openModal={openModal}
              setSelectedCards={setSelectedCards}
              setIsPrintGridModalOpen={setIsPrintGridModalOpen}
              selectedTableCards={selectedTableCards}
              setSelectedTableCards={setSelectedTableCards}
            />
          </Tile>
          <TableCardModal
            isOpen={isTableCardModalOpen}
            setIsOpen={setIsTableCardModalOpen}
            noEdit={noEdit}
            setNoEdit={setNoEdit}
            submitFunction={submitFunction}
          />
          <TablesModal
            isOpen={isTablesModalOpen}
            closeModal={handleTablesModalOnClose}
          />
          <PrintGridModal
            isOpen={isPrintGridModalOpen}
            setIsOpen={setIsPrintGridModalOpen}
            printRef={printRef}
            setSelectedRowIds={setSelectedRowIds}
            setSelectedTableCards={setSelectedTableCards}
          />
        </div>
      </Grid>
      {isPrintGridModalOpen && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: '-9999px',
            top: '-9999px',
            pointerEvents: 'none'
          }}
        >
          <CardsPrintLayout
            ref={printRef}
            cards={selectedCards || []}
            logoImageUrl={logoImageUrl}
          />
        </div>
      )}
    </>
  )
}

export default TableCardsManagement
